import { faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { Location } from "history";
import find from "lodash-es/find";
import flatten from "lodash-es/flatten";
import { denormalize } from "normalizr";
import { parse, stringify } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { analyticsStoreEvent } from "@/mk2/actions";
import { bazaarNavigationActivate, bazaarNavigationStoreBack } from "@/mk2/apps/bazaar/actions";
import { FacetsData } from "@/mk2/apps/bazaar/components/FacetsFilter";
import BazaarGrid from "@/mk2/apps/bazaar/containers/BazaarGrid/BazaarGrid";
import {
    Checked,
    bazaarGridLoad,
    bazaarGridWatchdogSubscribe,
    bazaarGridWatchdogUnsubscribe,
} from "@/mk2/apps/bazaar/containers/BazaarGrid/BazaarGrid.actions";
import reducer from "@/mk2/apps/bazaar/containers/BazaarGrid/BazaarGrid.reducers";
import saga from "@/mk2/apps/bazaar/containers/BazaarGrid/BazaarGrid.sagas";
import { getBazaarGridFilterState } from "@/mk2/apps/bazaar/containers/BazaarGrid/BazaarGrid.selectors";
import { deserializeFilter } from "@/mk2/apps/bazaar/helpers/filter";
import navigationReducer, { ScrollPosition } from "@/mk2/apps/bazaar/reducers/navigation";
import navigationSaga from "@/mk2/apps/bazaar/sagas/navigation";
import { ProductEntity } from "@/mk2/apps/bazaar/schemas";
import { getBazaarNavigationState } from "@/mk2/apps/bazaar/selectors/navigation";
import { breadcrumbsProduct } from "@/mk2/apps/bazaar/structuredData";
import { bazaarCreateProductUrl, bazaarGridUrl } from "@/mk2/apps/bazaar/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { AppName } from "@/mk2/containers/Application/Application";
import { ComplaintEntity, ComplaintSchema } from "@/mk2/containers/Complaint/Complaint.schemas";
import { cacheLast } from "@/mk2/helpers/cache";
import { showSuccessToastAction } from "@/mk2/helpers/toasts";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    InterstitialProps,
    PageRouteProps,
} from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestSettings,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    ComplaintsPerProductSchema,
    MenuSelection,
    PhotoDetailSchema,
    ProductSchema,
    StrollerEntity,
    StrollerPhoto,
    StrollerSchema,
    UserEntity,
} from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import {
    BAZAAR_INDEX_CATEGORY_DESCRIPTION_BABY,
    BAZAAR_INDEX_DESCRIPTION_BABY,
    BAZAAR_SEO_BABY,
    BZ2_ADD_PRODUCT,
    CONFIRM,
    PAGE,
} from "@/mk/autogenerated/translation";
import { hasAds } from "@/mk/bazaar/common/userUtils";
import { dict, getLabel, getTitleForCategoriesFilter } from "@/mk/bazaar/product/categories";
import { Filter } from "@/mk/bazaar/product/filter/filterUtils";

export interface OwnProps {}

export interface StateProps {
    requestUser: UserEntity;
    requestUserPermissions: string[];
    loadingState: LoadingState;
    errorMessage: string;
    filterFromRoute: Filter;
    hits: number; // products + strollers
    hitsAll: number;
    hitsLiked: number;
    facets: FacetsData;
    products: ProductEntity[];

    strollers: StrollerEntity[];
    strollerPhotosMap: Record<number, StrollerPhoto>;

    isMobile: boolean;
    baseUrl: string;
    complaintsPerProduct: {
        [productId: number]: ComplaintEntity[];
    };
    checked: Checked;
    scroll: ScrollPosition;
    watchdog: boolean;
    openedFacet: string;
}

export interface DispatchProps {
    onLoad(filter: Filter, watchdogKey: string);
    onStoreEvent(name: string, props: any);
    onWatchdogSubscribe(filter: Filter, watchdogKey: string);
    onWatchdogUnsubscribe(filter: Filter, watchdogKey: string);

    onNavigationStoreBack(back: Location, scroll: ScrollPosition);
    onNavigationActivate(hash: string);
    onShowSuccessToast(message: string);
}

export type RouteProps = PageRouteProps<{
    category?: string;
    filters?: string;
    page?: string;
}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class BazaarGridPage extends Page<OwnProps, StateProps, DispatchProps> {
    private filterFromRouteCache = cacheLast(true);
    private complaintsPerProductCache = cacheLast(true);

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const qp = parse(ownProps.location.search) || {};
            const openedFacet = qp.opened as string;

            const requestUserPermissions = getRequestPermissions(state);

            const settings = getRequestSettings(state);

            const filterFromRoute = this.filterFromRouteCache(
                () =>
                    deserializeFilter(
                        ownProps.match.params,
                        ownProps.location.search,
                        {
                            gridSort: settings.gridSort,
                            quality: settings.quality,
                            cleanSort: settings.cleanSort,
                            placesRadius: settings.placesRadius,
                        },
                        requestUserPermissions,
                    ),
                // Key is empty for first location on server
                ownProps.location.key || `${ownProps.location.pathname}${ownProps.location.search}`,
                settings.gridSort,
                settings.quality,
                settings.cleanSort,
                requestUserPermissions,
            );

            const filterState = getBazaarGridFilterState(state, filterFromRoute);

            const complaintsPerProduct = this.complaintsPerProductCache(
                () =>
                    state.entities[ComplaintSchema.key]
                        ? denormalize(
                              filterState.complaintsPerProductIds,
                              ComplaintsPerProductSchema,
                              state.entities,
                          )
                        : {},
                filterState.complaintsPerProductIds,
            );
            const navigationState = getBazaarNavigationState(state);

            const strollerPhotos = getDenormalizedEntities<StrollerPhoto>(
                state,
                PhotoDetailSchema,
                filterState.strollerPhotosIds,
            );
            const strollerPhotosMap = {};
            strollerPhotos.map(
                (strollerPhoto) => (strollerPhotosMap[strollerPhoto.id] = strollerPhoto),
            );
            return {
                requestUser: getRequestUser(state),
                requestUserPermissions,
                loadingState: filterState.loadingState,
                errorMessage: filterState.errorMessage,
                facets: filterState.facets,
                hits: filterState.hits,
                hitsAll: filterState.hitsAll,
                hitsLiked: filterState.hitsLiked,
                filterFromRoute,
                products: getDenormalizedEntities<ProductEntity>(
                    state,
                    ProductSchema,
                    filterState.productsIds,
                ),

                strollers: getDenormalizedEntities<StrollerEntity>(
                    state,
                    StrollerSchema,
                    filterState.strollersIdsOrdered,
                ),
                strollerPhotosMap,

                isMobile: getRequestDeviceMobile(state),
                baseUrl: getRequestBaseUrl(state),
                checked: filterState.checked,
                complaintsPerProduct,
                scroll: navigationState.scroll,
                watchdog: filterState.watchdog,
                openedFacet,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: bazaarGridLoad,
            onStoreEvent: analyticsStoreEvent,
            onNavigationStoreBack: bazaarNavigationStoreBack,
            onNavigationActivate: bazaarNavigationActivate,
            onWatchdogSubscribe: bazaarGridWatchdogSubscribe,
            onWatchdogUnsubscribe: bazaarGridWatchdogUnsubscribe,
            onShowSuccessToast: showSuccessToastAction,
        };
    }

    public getMetaTitle(props: Props): string {
        const filter = props.filterFromRoute;
        const facets = props.facets;

        if (!facets || !filter) {
            return BAZAAR_SEO_BABY;
        }

        let title;
        const filterPart = getFilterPart(filter, facets);
        if (filter.cats.length) {
            title = getTitleForCategoriesFilter(filter);
            if (filter.page > 1) {
                title += ` - ${PAGE} ${filter.page}`;
            }
        } else {
            title = filterPart;
        }
        return `${title ? title + " | " : ""}${BAZAAR_SEO_BABY}`;
    }

    public getMetaDescription(props: OwnProps & StateProps & DispatchProps): string {
        const filter = props.filterFromRoute;
        const facets = props.facets;

        if (!facets || !filter) {
            return BAZAAR_INDEX_DESCRIPTION_BABY;
        }

        let description;
        const filterPart = getFilterPart(filter, facets);

        if (filter.cats.length) {
            const categories = reconstructFacet(filter.cats, facets.cats);
            description = `${categories.join(", ")} | ${
                filterPart ? filterPart + " | " : ""
            }${BAZAAR_INDEX_CATEGORY_DESCRIPTION_BABY}`;
        } else {
            description = `${filterPart ? filterPart + " | " : ""}${BAZAAR_INDEX_DESCRIPTION_BABY}`;
        }

        return description;
    }

    public render(props: Props): false | JSX.Element {
        return <BazaarGrid {...props} />;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return !!props.openedFacet && props.isMobile;
    }

    public getDefaultBackPath(props: Props): string | null {
        return url(bazaarGridUrl, {});
    }

    public getMetaRobots(props: Props): string {
        if (props.location.search !== "") {
            return "noindex,follow";
        }

        return super.getMetaRobots(props);
    }

    public getHeaderRight(props: Props): React.ReactElement {
        // No button for cats select
        if (props.isMobile && props.openedFacet === "cats") {
            return null;
        } else if (props.isMobile && props.openedFacet) {
            const qs = parse(props.location.search) || {};
            const { opened, ...restQs } = qs;
            return (
                <Btn
                    label={CONFIRM}
                    data-cy="confirm-button"
                    link={{
                        ...props.location,
                        state: {
                            dontScrollOnPush: true,
                        },
                        search: stringify(restQs),
                    }}
                />
            );
        } else if (props.isMobile) {
            return (
                <Btn
                    type={BtnType.Blue}
                    link={bazaarCreateProductUrl}
                    label={BZ2_ADD_PRODUCT}
                    icon={faPlusCircle}
                />
            );
        }
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: app,
            activeItem: null,
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.bazaar.navigation", navigationReducer);
        store.injectSaga("containers.bazaar.navigation", navigationSaga);
        store.injectReducer("containers.bazaar.bazaarGrid", reducer);
        store.injectSaga("containers.bazaar.bazaarGrid", saga);
    }

    public getBrandingProps(props: Props): BrandingProps {
        // Mobile ad moved to grid content - only show desktop branding
        if (props.isMobile) {
            return null;
        }

        return {
            zone: "Market Section - Branding (Desktop)",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: "Market Section - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { products, strollers, requestUser, isMobile } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if ((products?.length || 0 + strollers?.length || 0) < 10) {
            return null;
        }

        return {
            zone: "Market Section - Anchor - Mobile",
        };
    }

    public getStructuredData(props: Props): object[] {
        const filter = props.filterFromRoute;
        const facets = props.facets;

        if (!filter || !facets) {
            return [];
        }

        let data;
        if (filter.cats.length) {
            const cat = dict()[filter.cats[0]];
            data = [
                breadcrumbsProduct(props.baseUrl, {
                    ...cat,
                    name: getLabel(cat.name),
                }),
            ];
        } else {
            data = [breadcrumbsProduct(props.baseUrl)];
        }

        return data;
    }
}

function reconstructFacet(selected, facet) {
    if (!selected) {
        return [];
    }

    return (
        selected
            // Filter facets listed in URL but not allowed in the given category
            .filter((sel) => find(facet, (f) => f[0] === sel) !== undefined)
            // Map facet name
            .map((sel) => {
                const fac = find(facet, (f) => f[0] === sel);
                return fac[fac.length === 3 ? 1 : 0];
            })
    );
}

function getFilterPart(filter: Filter, facets: FacetsData) {
    const parts = [];
    if (filter.q) {
        parts.push(filter.q);
    }

    return flatten([
        parts,
        reconstructFacet(filter.props.p_size, facets.p_size),
        reconstructFacet(filter.props.p_type, facets.p_type),
        reconstructFacet(filter.props.p_sex, facets.p_sex),
        reconstructFacet(filter.props.p_brand, facets.p_brand),
        reconstructFacet(filter.props.p_color, facets.p_color),
        reconstructFacet(filter.locations, facets.locations),
    ]).join(", ");
}
